import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe?email=${encodeURIComponent(email)}`);
      toast.success("Welcome to our community! You've been subscribed.");
      setEmail("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <footer className="bg-muted border-t border-border" data-testid="footer">
      <div className="container-custom section-spacing">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="block mb-6">
              <span className="font-serif text-2xl font-medium text-foreground">
                Sacred Sounds Studio
              </span>
            </Link>
            <p className="body-text text-muted-foreground text-sm leading-relaxed mb-6">
              Holistic healing through touch and sound. Experience deep relaxation
              and transformation in a nurturing space.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com/soundhealingmassage"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center border border-border hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                aria-label="Instagram"
                data-testid="instagram-link"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="mailto:hello@soundhealingmassage.com"
                className="w-10 h-10 flex items-center justify-center border border-border hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                aria-label="Email"
                data-testid="email-link"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-foreground">Services</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/sound-healing"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                  data-testid="footer-sound-healing-link"
                >
                  Sacred Sounds Studio
                </Link>
              </li>
              <li>
                <Link
                  to="/sway-with-sound"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                  data-testid="footer-sway-link"
                >
                  Sway with Sound
                </Link>
              </li>
              <li>
                <Link
                  to="/gift-vouchers"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                  data-testid="footer-vouchers-link"
                >
                  Gift Vouchers
                </Link>
              </li>
              <li>
                <Link
                  to="/booking"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                  data-testid="footer-booking-link"
                >
                  Book a Session
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-foreground">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">
                  Barking, Essex<br />
                  United Kingdom
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                <a
                  href="mailto:hello@soundhealingmassage.com"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                >
                  hello@soundhealingmassage.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm text-muted-foreground">
                  Available upon booking
                </span>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-foreground">
              Join Our Community
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              Subscribe for wellness tips, exclusive offers, and class updates.
            </p>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none border-border bg-background focus:ring-primary"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={isSubscribing}
                className="w-full rounded-none bg-primary hover:bg-primary/90 text-primary-foreground text-xs tracking-[0.15em] uppercase"
                data-testid="newsletter-submit-btn"
              >
                {isSubscribing ? "Subscribing..." : "Subscribe"}
              </Button>
            </form>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Sacred Sounds Studio. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <span className="text-xs text-muted-foreground">
              Privacy Policy
            </span>
            <span className="text-xs text-muted-foreground">
              <Link to="/terms" className="hover:text-primary transition-colors">
                Terms & Conditions
              </Link>
            </span>
            <span className="text-xs text-muted-foreground">
              <Link to="/cancellation-policy" className="hover:text-primary transition-colors">
                Cancellation Policy
              </Link>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
